import { useEffect, useState } from "react";
import type { Theme } from "./theme";
import { DEFAULT_THEME, migrateLegacyThemeId, themes } from "./theme";
import { syncUiCssVars } from "./syncUiCssVars";

const THEME_KEY = "app-theme";

export const resolveStoredThemeId = (raw: string | null): Theme => {
  if (raw == null || raw === "") return DEFAULT_THEME;
  const migrated = migrateLegacyThemeId(raw);
  const candidate = migrated ?? raw;
  return candidate in themes ? (candidate as Theme) : DEFAULT_THEME;
};

const applyThemeConfig = (id: Theme): void => {
  const themeConfig = themes[id];
  if (!themeConfig) return;
  const root = document.documentElement;
  for (const [key, value] of Object.entries(themeConfig)) {
    root.style.setProperty(key, value as string);
  }
  syncUiCssVars(themeConfig);
  root.setAttribute("data-theme", id);
};

/** Áp CSS vars trước React mount (tránh FOUC). */
export const bootstrapStoredThemeAppearance = (): void => {
  if (typeof document === "undefined") return;
  applyThemeConfig(resolveStoredThemeId(localStorage.getItem(THEME_KEY)));
};

const useThemeSwitcher = () => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(() =>
    resolveStoredThemeId(localStorage.getItem(THEME_KEY)),
  );

  useEffect(() => {
    const effectiveTheme = resolveStoredThemeId(currentTheme);

    if (effectiveTheme !== currentTheme) {
      setCurrentTheme(effectiveTheme);
      return;
    }

    applyThemeConfig(effectiveTheme);
    localStorage.setItem(THEME_KEY, effectiveTheme);
  }, [currentTheme]);

  const setTheme = (themeId: Theme) => {
    if (!(themeId in themes)) return;
    setCurrentTheme(themeId);
  };

  return { currentTheme, setTheme };
};

export default useThemeSwitcher;
